import React from 'react';
import { PU_GOLD_GRAD, PU_URGENCY } from '../../patient-urgency/config';
import type {
  PuAiSummary,
  PuPatient,
  PuPractice,
  PuUnscheduledTreatment,
} from '../../patient-urgency/types';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { PuAiStar } from './PuAiStar';
import { PuBenefitDateLine } from './PuBenefitDateLine';
import { PuCostBar } from './PuCostBar';
import { PuToothDiagram } from './PuToothDiagram';
import { PuVideoPlaceholder } from './PuVideoPlaceholder';
import {
  puBtnPrimary,
  puBtnSecondary,
  puCard,
  puDisplay,
  puEyebrow,
  puMono,
  puRise,
  puSpectrumBar,
  puTreatmentCard,
} from './puClasses';

export function PatientUrgencyPracticeHeader({ practice }: { practice: PuPractice }) {
  return (
    <header className={cn(puRise(0), 'mb-2 flex items-center gap-3 border-b border-[var(--cream-2)] pb-[22px]')}>
      <div
        className={cn(
          puDisplay,
          'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[15px] font-semibold tracking-[-0.02em] text-white shadow-[var(--sh-1)]',
        )}
        style={{ background: PU_GOLD_GRAD }}
      >
        {practice.logoMark}
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn(puDisplay, 'text-[15px] font-semibold tracking-[-0.015em] text-[var(--ink-0)]')}>
          {practice.name}
        </div>
        <div className="text-[11.5px] text-[var(--ink-3)]">{practice.tagline}</div>
      </div>
      <button
        type="button"
        className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border-0 bg-transparent px-2 py-1.5 text-xs font-medium text-[var(--ink-3)]"
      >
        <Icon kind="globe" size={13} /> {practice.language.current}
      </button>
    </header>
  );
}

export function PatientUrgencyHero({ patient }: { patient: PuPatient }) {
  const { used, annualMax: total, remaining, resetsInDays } = patient.insurance;
  const pct = Math.min(100, Math.round((used / total) * 100));

  return (
    <section className={cn(puRise(1), 'pb-[26px] pt-[22px]')}>
      <div className={cn(puEyebrow, 'mb-2.5 text-[var(--gold)]')}>Your treatment summary</div>
      <h1
        className={cn(
          puDisplay,
          'm-0 mb-3.5 text-[36px] font-semibold leading-[1.05] tracking-[-0.035em] text-[var(--ink-0)]',
        )}
      >
        Hi {patient.preferredName},<br />
        <span className="text-[25px] font-medium text-[var(--ink-2)]">
          you have{' '}
          <span className={cn(puMono, 'text-[27px] font-semibold tracking-[-0.02em] text-[var(--success)]')}>
            ${patient.unscheduledValue.toLocaleString()}
          </span>{' '}
          in care pending.
        </span>
      </h1>

      <div className="mt-[18px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[18px] py-4 shadow-[var(--sh-1)]">
        <div className="mb-2.5 flex items-center justify-between gap-2 text-xs">
          <span className="font-semibold text-[var(--ink-3)]">
            {patient.insurance.carrier} · benefit year
          </span>
          <span className={cn(puMono, 'whitespace-nowrap font-medium text-[var(--ink-2)]')}>
            ${used.toLocaleString()} / ${total.toLocaleString()} used
          </span>
        </div>
        <div className="mb-3.5 h-2 overflow-hidden rounded-full bg-[var(--surface-mute)]">
          <div
            className="h-full rounded-full bg-[linear-gradient(90deg,color-mix(in_srgb,var(--success)_62%,#fff),var(--success))]"
            style={{ width: `${pct}%` }}
          />
        </div>
        <PuBenefitDateLine resetsInDays={resetsInDays} />
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[var(--cream-2)] pt-3.5 text-[13.5px] leading-snug text-[var(--ink-2)]">
          <strong className={cn(puMono, 'text-[15px] font-bold text-[var(--success)]')}>
            ${remaining.toLocaleString()}
          </strong>
          <span>remaining · resets in</span>
          <strong className="text-[var(--ink-0)]">{resetsInDays} days</strong>
        </div>
      </div>
    </section>
  );
}

export function PatientUrgencyAISummary({
  summary,
  practiceName,
}: {
  summary: PuAiSummary;
  practiceName: string;
}) {
  return (
    <section
      className={cn(
        puCard,
        puRise(2),
        'mb-8 overflow-hidden bg-[linear-gradient(180deg,var(--cream-1)_0%,color-mix(in_srgb,var(--ai-3)_3%,var(--cream-1))_100%)] p-0',
      )}
    >
      <div className={puSpectrumBar} />
      <div className="px-[22px] pb-5 pt-[22px]">
        <div className="mb-3.5 flex items-center gap-2">
          <PuAiStar size={16} />
          <div className={cn(puEyebrow, 'text-[var(--ai-3)]')}>{practiceName} AI summary</div>
        </div>
        <p className="m-0 mb-[18px] text-[16.5px] leading-[1.65] text-[var(--ink-1)]">{summary.body}</p>
        {summary.callout ? (
          <div className="flex items-start gap-2.5 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--success)_20%,transparent)] bg-[var(--success-tint)] px-[15px] py-3">
            <Icon kind="calendar" size={15} className="mt-0.5 shrink-0 text-[var(--success)]" />
            <p className="m-0 text-sm font-medium leading-[1.55] text-[color-mix(in_srgb,var(--success)_88%,#000)]">
              {summary.callout}
            </p>
          </div>
        ) : null}
        <div className={cn(puMono, 'mt-3.5 text-[11px] tracking-[0.02em] text-[var(--ink-4)]')}>
          Generated by ELVA AI · {summary.generatedAt}
        </div>
      </div>
    </section>
  );
}

export function PatientUrgencyTreatmentCard({
  tx,
  delayIndex,
}: {
  tx: PuUnscheduledTreatment;
  delayIndex: number;
}) {
  const u = PU_URGENCY[tx.urgencyTier] ?? PU_URGENCY.standard;
  const isHigh = tx.urgencyTier === 'high';
  const isStd = tx.urgencyTier === 'standard';

  return (
    <article
      className={cn(
        puCard,
        puTreatmentCard,
        puRise(delayIndex),
        'mb-3.5',
        isStd ? 'px-5 py-[18px]' : 'p-[22px]',
        isHigh && 'shadow-[var(--sh-1),0_0_0_1px_color-mix(in_srgb,var(--terracotta)_12%,transparent)]',
      )}
    >
      <div
        className="mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11px] font-semibold"
        style={{ background: u.bg, color: u.fg, border: `1px solid ${u.border}` }}
      >
        <Icon kind={u.icon} size={11} stroke={2} />
        {tx.urgencyLabel}
      </div>

      {tx.toothNumber ? (
        <div className="mb-4 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-2 py-2.5">
          <PuToothDiagram
            toothNumber={tx.toothNumber}
            highlightColor={u.highlight}
            size={isStd ? 'sm' : 'md'}
          />
        </div>
      ) : null}

      <h2
        className={cn(
          puDisplay,
          'm-0 mb-1 font-semibold leading-[1.25] tracking-[-0.02em] text-[var(--ink-0)]',
          isStd ? 'text-lg' : 'text-xl',
        )}
      >
        {tx.title}
      </h2>
      <div className="mb-[18px] text-[13.5px] text-[var(--ink-3)]">{tx.subtitle}</div>

      {tx.video ? (
        <div className="mb-[18px]">
          <PuVideoPlaceholder video={tx.video} />
        </div>
      ) : null}

      <div className="mb-4">
        <div className={cn(puEyebrow, 'mb-1.5')}>Why this matters</div>
        <p className={cn('m-0 leading-[1.6] text-[var(--ink-2)]', isStd ? 'text-sm' : 'text-[14.5px]')}>
          {tx.whyMatters}
        </p>
      </div>

      {tx.doctorNote ? (
        <div className="mb-[18px] rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--ai-3)_18%,transparent)] bg-[color-mix(in_srgb,var(--ai-3)_4%,var(--cream-1))] px-[18px] py-4">
          <div className="mb-2 flex items-center gap-2">
            <div
              className={cn(
                puDisplay,
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--grad-spectrum)] text-[10.5px] font-bold text-white',
              )}
            >
              {tx.doctorNote.by.replace('Dr. ', '').charAt(0)}R
            </div>
            <div className={cn(puEyebrow, 'text-[var(--ai-3)]')}>A note from {tx.doctorNote.by}</div>
          </div>
          <p className="m-0 text-base italic leading-[1.55] text-[var(--ink-1)]">
            “{tx.doctorNote.body}”
          </p>
          <div className={cn(puMono, 'mt-2.5 text-[11px] text-[var(--ink-4)]')}>{tx.doctorNote.date}</div>
        </div>
      ) : null}

      <div className="mb-[18px] rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-3.5">
        <div className={cn(puEyebrow, 'mb-[11px]')}>Estimated cost</div>
        <PuCostBar fee={tx.fee} insurance={tx.insurance} patient={tx.patientEst} />
        <div className="mt-3.5 flex items-baseline justify-between gap-2.5 border-t border-[var(--cream-2)] pt-3">
          <span className="text-[12.5px] text-[var(--ink-3)]">
            ${tx.fee.toLocaleString()} fee · ${tx.insurance.toLocaleString()} covered
          </span>
          <div className="text-right">
            <div className={cn(puEyebrow, 'mb-px text-[9px]')}>Your portion</div>
            <span
              className={cn(
                puMono,
                'text-[22px] font-bold leading-none tracking-[-0.01em] text-[var(--success)]',
              )}
            >
              ${tx.patientEst.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <a href={tx.bookingLink} className={cn(puBtnPrimary, 'no-underline')}>
        Book this treatment <Icon kind="arrow-r" size={15} />
      </a>
      <div className="mt-3 text-center text-[11.5px] text-[var(--ink-4)]">
        Recommended {tx.diagnosed}
      </div>
    </article>
  );
}

export function PatientUrgencyTreatmentsList({
  treatments,
}: {
  treatments: PuUnscheduledTreatment[];
}) {
  return (
    <section className="mb-7">
      <div className={cn(puRise(3), 'mb-3.5')}>
        <div className={cn(puEyebrow, 'mb-1.5')}>What’s pending</div>
        <h2 className={cn(puDisplay, 'm-0 text-[22px] font-semibold tracking-[-0.025em] text-[var(--ink-0)]')}>
          {treatments.length} treatments{' '}
          <span className="font-medium text-[var(--ink-2)]">your dentist recommended</span>
        </h2>
      </div>
      {treatments.map((tx, i) => (
        <PatientUrgencyTreatmentCard key={tx.id} tx={tx} delayIndex={Math.min(i + 4, 7)} />
      ))}
    </section>
  );
}

export function PatientUrgencyBookingCTA({ practice }: { practice: PuPractice }) {
  return (
    <section
      className={cn(
        puCard,
        puRise(5),
        'mb-[22px] border-[color-mix(in_srgb,var(--success)_20%,transparent)] bg-[linear-gradient(180deg,var(--cream-1)_0%,var(--success-tint)_100%)] px-[22px] py-6',
      )}
    >
      <div className={cn(puEyebrow, 'mb-2 text-[var(--success)]')}>Ready to schedule?</div>
      <h2
        className={cn(
          puDisplay,
          'm-0 mb-2.5 text-[22px] font-semibold leading-[1.25] tracking-[-0.025em] text-[var(--ink-0)]',
        )}
      >
        Book any of these online, right now.
      </h2>
      <p className="m-0 mb-[18px] text-[14.5px] leading-[1.55] text-[var(--ink-2)]">
        Our scheduling is online — pick a time that works for you. Most appointments can be booked
        in under a minute.
      </p>
      <a href={practice.bookingUrl} className={cn(puBtnPrimary, 'mb-2.5 no-underline')}>
        Book online <Icon kind="arrow-r" size={15} />
      </a>
      <a href={`tel:${practice.phone}`} className={cn(puBtnSecondary, 'no-underline')}>
        <Icon kind="phone" size={14} /> Or call us at {practice.phone}
      </a>
    </section>
  );
}

export function PatientUrgencyFooter({ practice }: { practice: PuPractice }) {
  return (
    <footer className={cn(puRise(6), 'mt-[30px] border-t border-[var(--cream-2)] pt-[22px] text-center')}>
      <div className="mb-3 flex items-center justify-center gap-2">
        <div
          className={cn(
            puDisplay,
            'inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-semibold tracking-[-0.02em] text-white',
          )}
          style={{ background: PU_GOLD_GRAD }}
        >
          {practice.logoMark}
        </div>
        <span className="text-[13px] font-semibold text-[var(--ink-0)]">{practice.name}</span>
      </div>
      <div className="mb-4 flex flex-wrap justify-center gap-x-3.5 gap-y-1 px-3 text-[12.5px] leading-[1.6] text-[var(--ink-3)]">
        <span className="inline-flex items-center gap-1.5">
          <Icon kind="pin" size={11} /> {practice.address}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Icon kind="phone" size={11} /> {practice.phone}
        </span>
      </div>
      <div className="flex items-center justify-center gap-2 px-3 text-[11px] leading-[1.55] text-[var(--ink-4)]">
        <Icon kind="lock" size={11} />
        <span>This page is private to you. Return via the link in your text message.</span>
      </div>
    </footer>
  );
}
